import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, TrendingUp, Users, MousePointer, Clock, ExternalLink } from 'lucide-react';

// MetricCard component moved outside
const MetricCard = ({ icon: Icon, title, value, subtitle, color = '#00ffff' }) => (
  <div style={{
    background: 'rgba(0, 0, 0, 0.4)',
    border: `2px solid ${color}33`,
    borderRadius: '8px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    transition: 'all 0.3s ease',
    cursor: 'pointer'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.borderColor = color;
    e.currentTarget.style.transform = 'translateY(-4px)';
    e.currentTarget.style.boxShadow = `0 8px 24px ${color}33`;
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.borderColor = `${color}33`;
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = 'none';
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ color: '#999', fontSize: '14px', fontWeight: '600' }}>{title}</span>
      <Icon size={24} color={color} />
    </div>
    <div style={{ color: '#fff', fontSize: '32px', fontWeight: '700' }}>{value}</div>
    {subtitle && <div style={{ color: '#666', fontSize: '12px' }}>{subtitle}</div>}
  </div>
);

const AnalyticsDashboard = ({ backendUrl, token }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30); // days

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/analytics/summary?days=${period}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, token, period]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  if (loading) {
    return <div style={{ color: '#00ffff', padding: '20px' }}>Завантаження статистики...</div>;
  }

  if (!analytics) {
    return <div style={{ color: '#ff0000', padding: '20px' }}>Помилка завантаження статистики</div>;
  }

  return (
    <div style={{ padding: '20px', color: '#fff' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#00ffff', marginBottom: '8px' }}>
          Статистика та Аналітика
        </h2>
        <p style={{ color: '#999', fontSize: '14px' }}>
          Період: останні {period} днів
        </p>
        
        {/* Period selector */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
          {[7, 30, 90].map(days => (
            <button
              key={days}
              onClick={() => setPeriod(days)}
              style={{
                padding: '8px 16px',
                background: period === days ? '#00ffff' : 'rgba(0, 255, 255, 0.1)',
                color: period === days ? '#000' : '#00ffff',
                border: '1px solid #00ffff',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
            >
              {days} днів
            </button>
          ))}
        </div>
      </div>

      {/* Main Metrics */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        <MetricCard
          icon={Users}
          title="Перегляди сторінки"
          value={analytics.total_page_views}
          subtitle={`${analytics.unique_sessions} унікальних сесій`}
          color="#00ffff"
        />
        <MetricCard
          icon={MousePointer}
          title="Кліки по кнопках"
          value={analytics.total_button_clicks}
          subtitle="Всього інтерактивних дій"
          color="#ff00ff"
        />
        <MetricCard
          icon={TrendingUp}
          title="Конверсія"
          value={`${analytics.conversion_rate}%`}
          subtitle={`${analytics.total_registrations} реєстрацій`}
          color="#00ff00"
        />
        <MetricCard
          icon={Clock}
          title="Середній час на сайті"
          value={`${Math.floor(analytics.avg_time_on_site / 60)}м ${Math.floor(analytics.avg_time_on_site % 60)}с`}
          subtitle="На одну сесію"
          color="#ffaa00"
        />
      </div>

      {/* Registrations Breakdown */}
      <div style={{
        background: 'rgba(0, 0, 0, 0.4)',
        border: '2px solid rgba(0, 255, 255, 0.2)',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '30px'
      }}>
        <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#00ffff', marginBottom: '20px' }}>
          Реєстрації
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          <div style={{ 
            padding: '16px', 
            background: 'rgba(0, 255, 255, 0.05)', 
            borderRadius: '6px',
            border: '1px solid rgba(0, 255, 255, 0.2)'
          }}>
            <div style={{ color: '#999', fontSize: '14px', marginBottom: '8px' }}>Як спікер</div>
            <div style={{ color: '#00ffff', fontSize: '28px', fontWeight: '700' }}>
              {analytics.speaker_registrations}
            </div>
          </div>
          <div style={{ 
            padding: '16px', 
            background: 'rgba(255, 0, 255, 0.05)', 
            borderRadius: '6px',
            border: '1px solid rgba(255, 0, 255, 0.2)'
          }}>
            <div style={{ color: '#999', fontSize: '14px', marginBottom: '8px' }}>Як учасник</div>
            <div style={{ color: '#ff00ff', fontSize: '28px', fontWeight: '700' }}>
              {analytics.participant_registrations}
            </div>
          </div>
          <div style={{ 
            padding: '16px', 
            background: 'rgba(0, 255, 0, 0.05)', 
            borderRadius: '6px',
            border: '1px solid rgba(0, 255, 0, 0.2)'
          }}>
            <div style={{ color: '#999', fontSize: '14px', marginBottom: '8px' }}>Всього реєстрацій</div>
            <div style={{ color: '#00ff00', fontSize: '28px', fontWeight: '700' }}>
              {analytics.total_registrations}
            </div>
          </div>
        </div>
      </div>

      {/* NEW: Returning vs New Visitors */}
      <div style={{
        background: 'rgba(0, 0, 0, 0.4)',
        border: '2px solid rgba(0, 255, 255, 0.2)',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '30px'
      }}>
        <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#00ffff', marginBottom: '20px' }}>
          Повторні vs Нові відвідувачі
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          <div style={{ 
            padding: '20px', 
            background: 'rgba(0, 255, 255, 0.05)', 
            borderRadius: '6px',
            border: '1px solid rgba(0, 255, 255, 0.2)'
          }}>
            <div style={{ color: '#999', fontSize: '14px', marginBottom: '8px' }}>Нові відвідувачі</div>
            <div style={{ color: '#00ffff', fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
              {analytics.new_visitors}
            </div>
            <div style={{ color: '#00ffff', fontSize: '16px' }}>
              {analytics.visitor_type_ratio?.new || 0}%
            </div>
          </div>
          <div style={{ 
            padding: '20px', 
            background: 'rgba(255, 0, 255, 0.05)', 
            borderRadius: '6px',
            border: '1px solid rgba(255, 0, 255, 0.2)'
          }}>
            <div style={{ color: '#999', fontSize: '14px', marginBottom: '8px' }}>Повторні відвідувачі</div>
            <div style={{ color: '#ff00ff', fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
              {analytics.returning_visitors}
            </div>
            <div style={{ color: '#ff00ff', fontSize: '16px' }}>
              {analytics.visitor_type_ratio?.returning || 0}%
            </div>
          </div>
        </div>
      </div>

      {/* NEW: Device Breakdown */}
      {analytics.device_breakdown && analytics.device_breakdown.length > 0 && (
        <div style={{
          background: 'rgba(0, 0, 0, 0.4)',
          border: '2px solid rgba(0, 255, 255, 0.2)',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '30px'
        }}>
          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#00ffff', marginBottom: '20px' }}>
            Типи пристроїв
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
            {(() => {
              const totalDevices = analytics.device_breakdown.reduce((sum, d) => sum + d.count, 0);
              return analytics.device_breakdown.map((device) => (
                <div key={device.device} style={{
                  padding: '16px',
                  background: 'rgba(0, 255, 255, 0.05)',
                  borderRadius: '6px',
                  border: '1px solid rgba(0, 255, 255, 0.1)',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#999', fontSize: '13px', marginBottom: '8px' }}>{device.device}</div>
                  <div style={{ color: '#00ffff', fontSize: '24px', fontWeight: '700' }}>
                    {device.count}
                  </div>
                  <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                    {totalDevices > 0 ? Math.round((device.count / totalDevices) * 100) : 0}%
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* NEW: Conversion Funnel */}
      {analytics.conversion_funnel && analytics.conversion_funnel.length > 0 && (
        <div style={{
          background: 'rgba(0, 0, 0, 0.4)',
          border: '2px solid rgba(0, 255, 255, 0.2)',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '30px'
        }}>
          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#00ffff', marginBottom: '20px' }}>
            Воронка конверсії
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {analytics.conversion_funnel.map((step, index) => {
              const maxCount = analytics.conversion_funnel[0].count;
              const widthPercent = (step.count / maxCount) * 100;
              
              return (
                <div key={index} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>
                      {index + 1}. {step.step}
                    </span>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <span style={{ color: '#00ffff', fontSize: '16px', fontWeight: '600' }}>
                        {step.count}
                      </span>
                      <span style={{ color: '#00ff00', fontSize: '14px' }}>
                        {step.conversion_rate}%
                      </span>
                      {step.drop_off_rate > 0 && (
                        <span style={{ color: '#ff6b6b', fontSize: '14px' }}>
                          ↓{step.drop_off_rate}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '32px',
                    background: 'rgba(0, 255, 255, 0.1)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <div style={{
                      width: `${widthPercent}%`,
                      height: '100%',
                      background: `linear-gradient(90deg, #00ffff ${50 - index * 10}%, #ff00ff ${100 - index * 10}%)`,
                      transition: 'width 0.5s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      paddingRight: '12px'
                    }}>
                      <span style={{ color: '#fff', fontSize: '12px', fontWeight: '600' }}>
                        {Math.round(widthPercent)}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* NEW: Geographic Data */}
      {analytics.countries && analytics.countries.length > 0 && (
        <div style={{
          background: 'rgba(0, 0, 0, 0.4)',
          border: '2px solid rgba(0, 255, 255, 0.2)',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '30px'
        }}>
          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#00ffff', marginBottom: '20px' }}>
            Географія відвідувачів
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {/* Countries */}
            <div>
              <h4 style={{ color: '#999', fontSize: '14px', marginBottom: '12px', fontWeight: '600' }}>Країни</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {analytics.countries.slice(0, 5).map((country, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    background: 'rgba(0, 255, 255, 0.03)',
                    borderRadius: '4px',
                    border: '1px solid rgba(0, 255, 255, 0.1)'
                  }}>
                    <span style={{ color: '#fff', fontSize: '13px' }}>{country.country}</span>
                    <span style={{ color: '#00ffff', fontSize: '14px', fontWeight: '600' }}>
                      {country.visitors}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Cities */}
            {analytics.cities && analytics.cities.length > 0 && (
              <div>
                <h4 style={{ color: '#999', fontSize: '14px', marginBottom: '12px', fontWeight: '600' }}>Міста</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {analytics.cities.slice(0, 5).map((city, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      background: 'rgba(255, 0, 255, 0.03)',
                      borderRadius: '4px',
                      border: '1px solid rgba(255, 0, 255, 0.1)'
                    }}>
                      <span style={{ color: '#fff', fontSize: '13px' }}>{city.city}</span>
                      <span style={{ color: '#ff00ff', fontSize: '14px', fontWeight: '600' }}>
                        {city.visitors}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* NEW: User Journey & Exit Pages */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
        {/* User Journey */}
        {analytics.user_journey && analytics.user_journey.length > 0 && (
          <div style={{
            background: 'rgba(0, 0, 0, 0.4)',
            border: '2px solid rgba(0, 255, 255, 0.2)',
            borderRadius: '8px',
            padding: '24px'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#00ffff', marginBottom: '16px' }}>
              Шлях користувача
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {analytics.user_journey.slice(0, 8).map((path, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  background: 'rgba(0, 255, 255, 0.03)',
                  borderRadius: '4px',
                  border: '1px solid rgba(0, 255, 255, 0.1)'
                }}>
                  <span style={{ color: '#fff', fontSize: '12px' }}>
                    {path.from} → {path.to}
                  </span>
                  <span style={{ color: '#00ffff', fontSize: '13px', fontWeight: '600' }}>
                    {path.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Exit Pages */}
        {analytics.exit_pages && analytics.exit_pages.length > 0 && (
          <div style={{
            background: 'rgba(0, 0, 0, 0.4)',
            border: '2px solid rgba(255, 0, 255, 0.2)',
            borderRadius: '8px',
            padding: '24px'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#ff00ff', marginBottom: '16px' }}>
              Сторінки виходу
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {analytics.exit_pages.slice(0, 8).map((exit, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  background: 'rgba(255, 0, 255, 0.03)',
                  borderRadius: '4px',
                  border: '1px solid rgba(255, 0, 255, 0.1)'
                }}>
                  <span style={{ color: '#fff', fontSize: '12px' }}>{exit.page}</span>
                  <span style={{ color: '#ff00ff', fontSize: '13px', fontWeight: '600' }}>
                    {exit.exits}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Button Clicks Breakdown */}
      {Object.keys(analytics.button_clicks_breakdown).length > 0 && (
        <div style={{
          background: 'rgba(0, 0, 0, 0.4)',
          border: '2px solid rgba(0, 255, 255, 0.2)',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '30px'
        }}>
          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#00ffff', marginBottom: '20px' }}>
            Популярні кнопки
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {Object.entries(analytics.button_clicks_breakdown)
              .sort(([, a], [, b]) => b - a)
              .map(([button, clicks]) => (
                <div key={button} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: 'rgba(0, 255, 255, 0.05)',
                  borderRadius: '6px',
                  border: '1px solid rgba(0, 255, 255, 0.1)'
                }}>
                  <span style={{ color: '#fff', fontSize: '14px' }}>{button}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: `${Math.min(clicks * 10, 200)}px`,
                      height: '8px',
                      background: 'linear-gradient(90deg, #00ffff, #ff00ff)',
                      borderRadius: '4px'
                    }} />
                    <span style={{ color: '#00ffff', fontSize: '16px', fontWeight: '600', minWidth: '40px', textAlign: 'right' }}>
                      {clicks}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Traffic Sources by Category */}
      {analytics.traffic_by_category && analytics.traffic_by_category.length > 0 && (
        <div style={{
          background: 'rgba(0, 0, 0, 0.4)',
          border: '2px solid rgba(255, 215, 0, 0.2)',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '30px'
        }}>
          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#FFD700', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            🔍 Категорії джерел трафіку
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '30px' }}>
            {analytics.traffic_by_category.map((cat, index) => {
              const colors = {
                'Direct': '#00ffff',
                'Search': '#00ff00',
                'Social': '#ff00ff',
                'Referral': '#ffa500',
                'Unknown': '#999'
              };
              const color = colors[cat.category] || '#00ffff';
              const icons = {
                'Direct': '🔗',
                'Search': '🔍',
                'Social': '💬',
                'Referral': '🌐',
                'Unknown': '❓'
              };
              const icon = icons[cat.category] || '🌐';
              
              return (
                <div key={index} style={{
                  background: `rgba(0, 0, 0, 0.3)`,
                  border: `2px solid ${color}33`,
                  borderRadius: '8px',
                  padding: '16px',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = color;
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = `${color}33`;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>{icon}</div>
                  <div style={{ color: '#fff', fontSize: '14px', marginBottom: '8px', fontWeight: '600' }}>
                    {cat.category}
                  </div>
                  <div style={{ color: color, fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>
                    {cat.sessions}
                  </div>
                  <div style={{ color: '#666', fontSize: '12px' }}>
                    {cat.percentage}% трафіку
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Detailed Traffic Sources */}
          {analytics.traffic_by_source && analytics.traffic_by_source.length > 0 && (
            <>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#FFD700', marginBottom: '16px', marginTop: '20px' }}>
                📊 Детальні джерела
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {analytics.traffic_by_source.slice(0, 10).map((source, index) => {
                  const maxSessions = analytics.traffic_by_source[0]?.sessions || 1;
                  const barWidth = (source.sessions / maxSessions) * 100;
                  
                  return (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 14px',
                      background: 'rgba(255, 215, 0, 0.03)',
                      borderRadius: '4px',
                      border: '1px solid rgba(255, 215, 0, 0.1)'
                    }}>
                      <div style={{ flex: 1, marginRight: '16px' }}>
                        <div style={{ color: '#fff', fontSize: '13px', marginBottom: '6px' }}>
                          {source.source}
                        </div>
                        <div style={{
                          width: '100%',
                          height: '4px',
                          background: 'rgba(255, 215, 0, 0.1)',
                          borderRadius: '2px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${barWidth}%`,
                            height: '100%',
                            background: 'linear-gradient(90deg, #FFD700, #FFA500)',
                            borderRadius: '2px',
                            transition: 'width 0.5s ease'
                          }} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#FFD700', fontSize: '14px', fontWeight: '600' }}>
                          {source.sessions}
                        </span>
                        <span style={{ color: '#666', fontSize: '12px' }}>
                          ({source.percentage}%)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Top Referrers */}
      {analytics.top_referrers.length > 0 && (
        <div style={{
          background: 'rgba(0, 0, 0, 0.4)',
          border: '2px solid rgba(0, 255, 255, 0.2)',
          borderRadius: '8px',
          padding: '24px'
        }}>
          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#00ffff', marginBottom: '20px' }}>
            Джерела трафіку
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {analytics.top_referrers.map((ref, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                background: 'rgba(0, 255, 255, 0.03)',
                borderRadius: '4px',
                border: '1px solid rgba(0, 255, 255, 0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ExternalLink size={16} color="#00ffff" />
                  <span style={{ color: '#fff', fontSize: '13px' }}>
                    {ref.referrer || 'Прямий трафік'}
                  </span>
                </div>
                <span style={{ color: '#00ffff', fontSize: '14px', fontWeight: '600' }}>
                  {ref.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
